// @ts-nocheck
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

const RANGE_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "365", label: "Last year" },
  { value: "all", label: "All time" },
];

export default function AdPerformancePage() {
  const [dateRange, setDateRange] = useState("30");

  const startDate = useMemo(() => {
    if (dateRange === "all") return new Date(2020, 0, 1).toISOString();
    const days = parseInt(dateRange, 10);
    return new Date(Date.now() - days * 86400000).toISOString();
  }, [dateRange]);

  const queryClient = useQueryClient();

  const { data: summary } = useQuery({
    queryKey: ["ad-performance-summary", startDate],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_ad_performance_summary", {
        p_start_date: startDate,
      });
      if (error) throw error;
      return data?.[0] || { total_impressions: 0, total_clicks: 0, active_asset_count: 0 };
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const { data: timeSeries = [] } = useQuery({
    queryKey: ["ad-performance-timeseries", startDate],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_ad_performance_timeseries", {
        p_start_date: startDate,
      });
      if (error) throw error;
      return data || [];
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const { data: perAsset = [] } = useQuery({
    queryKey: ["ad-performance-per-asset", startDate],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_ad_performance_per_asset", {
        p_start_date: startDate,
      });
      if (error) throw error;
      return data || [];
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const totalImpressions = Number(summary?.total_impressions || 0);
  const totalClicks = Number(summary?.total_clicks || 0);
  const ctr = totalImpressions > 0
    ? `${((totalClicks / totalImpressions) * 100).toFixed(2)}%`
    : "—";
  const activeAssets = Number(summary?.active_asset_count || 0);

  const summaryStats = [
    { label: "Total Impressions", value: totalImpressions.toLocaleString() },
    { label: "Total Clicks", value: totalClicks.toLocaleString() },
    { label: "CTR", value: ctr },
    { label: "Active Assets", value: activeAssets.toLocaleString() },
  ];

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-heading-2 text-ink-primary">Ad Performance</h1>
          <p className="text-body-md text-ink-secondary mt-1">
            Impression and click data for all banner assets. Use this to report performance to advertisers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md"
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ["ad-performance-summary"] });
              queryClient.invalidateQueries({ queryKey: ["ad-performance-timeseries"] });
              queryClient.invalidateQueries({ queryKey: ["ad-performance-per-asset"] });
            }}
            className="inline-flex items-center gap-2 bg-surface-page border border-stroke-subtle hover:border-cyan px-3 py-2 rounded-token-md text-body-sm text-ink-primary hover:text-cyan transition-colors"
            title="Refresh data"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {summaryStats.map((stat) => (
          <div
            key={stat.label}
            className="bg-surface-raised border border-stroke-subtle rounded-token-xl shadow-token-sm p-5"
          >
            <div className="text-caption text-ink-tertiary uppercase tracking-wide">{stat.label}</div>
            <div className="text-heading-2 text-ink-primary mt-2 tabular-nums">{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Time-series chart */}
      <div className="bg-surface-raised border border-stroke-subtle rounded-token-xl shadow-token-sm p-6">
        <h2 className="text-heading-4 text-ink-primary mb-4">Performance Over Time</h2>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={timeSeries} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="impressions" stroke="#002E62" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="clicks" stroke="#BC8D09" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-asset table */}
      <div className="bg-surface-raised border border-stroke-subtle rounded-token-xl shadow-token-sm p-6">
        <h2 className="text-heading-4 text-ink-primary mb-4">By Asset</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-body-sm">
            <thead>
              <tr className="text-left text-ink-tertiary border-b border-stroke-subtle">
                <th className="py-2 pr-4 font-medium">Asset</th>
                <th className="py-2 pr-4 font-medium">Advertiser</th>
                <th className="py-2 pr-4 font-medium text-right">Impressions</th>
                <th className="py-2 pr-4 font-medium text-right">Clicks</th>
                <th className="py-2 pr-4 font-medium text-right">CTR</th>
              </tr>
            </thead>
            <tbody>
              {perAsset.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-ink-tertiary">
                    No data for this date range.
                  </td>
                </tr>
              )}
              {perAsset.map((row: any) => (
                <tr key={row.asset_id} className="border-b border-stroke-subtle">
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-3">
                      {row.image_url && (
                        <img
                          src={row.image_url}
                          alt={row.name}
                          className="w-12 h-8 object-cover rounded-token-sm bg-surface-sunken"
                        />
                      )}
                      <span className="text-ink-primary">{row.name}</span>
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-ink-secondary">{row.advertiser_name || "—"}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{Number(row.impressions).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{Number(row.clicks).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{row.ctr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
